import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import AppInput from '../Inputs/AppInput';
import { apiSearchProject } from '../../api/search';
import CONFIG from '../../config';

function renderMarkedSnippet(text, markerStart, markerEnd, matchKind = null) {
  const snippet = String(text || '').trim();
  if (!snippet) return null;

  const start = String(markerStart || '[[MATCH_START]]');
  const end = String(markerEnd || '[[MATCH_END]]');

  if (!start || !end || !snippet.includes(start) || !snippet.includes(end)) {
    return snippet;
  }

  const nodes = [];
  let cursor = 0;
  let key = 0;
  const isFuzzyMatch = String(matchKind || '').trim().toLowerCase() === 'fuzzy';
  const highlightClassName = isFuzzyMatch
    ? 'searchResultHighlight searchResultHighlightSimilar'
    : 'searchResultHighlight';

  while (cursor < snippet.length) {
    const startIndex = snippet.indexOf(start, cursor);
    if (startIndex < 0) {
      nodes.push(<React.Fragment key={`txt-${key++}`}>{snippet.slice(cursor)}</React.Fragment>);
      break;
    }

    if (startIndex > cursor) {
      nodes.push(<React.Fragment key={`txt-${key++}`}>{snippet.slice(cursor, startIndex)}</React.Fragment>);
    }

    const matchStart = startIndex + start.length;
    const endIndex = snippet.indexOf(end, matchStart);
    if (endIndex < 0) {
      nodes.push(<React.Fragment key={`txt-${key++}`}>{snippet.slice(startIndex)}</React.Fragment>);
      break;
    }

    const matchText = snippet.slice(matchStart, endIndex);
    if (matchText) {
      nodes.push(<mark key={`hl-${key++}`} className={highlightClassName}>{matchText}</mark>);
    }

    cursor = endIndex + end.length;
  }

  return nodes;
}

function formatSimilarityBadgeText(match) {
  const similarity = Number(match?.similarity);
  if (String(match?.kind || '').trim().toLowerCase() !== 'fuzzy') return null;
  if (!Number.isFinite(similarity)) return null;
  const pct = Math.max(0, Math.min(99, Math.round(similarity * 100)));
  return `~${pct}% similar`;
}

export default function SearchDialog({
  isOpen,
  onClose,
  projectId,
  onSelectResult
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const searchDebounceRef = useRef(null);
  const searchInputRef = useRef(null);
  const typeMinQueryLength = Math.max(1, Number(CONFIG.SEARCH.TYPE_MIN_QUERY_LENGTH || 3));

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      const input = searchInputRef.current;
      if (!input) return;
      input.focus();
      input.select();
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleRunSearch = async (queryOverride = null) => {
    const queryFromOverride = typeof queryOverride === 'string' ? queryOverride : null;
    const query = String(queryFromOverride ?? searchQuery).trim();

    if (!projectId || !query) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError('');

    try {
      const payload = await apiSearchProject(projectId, query, {
        profile: CONFIG.SEARCH.ACTIVE_PROFILE,
        scope: CONFIG.SEARCH.DEFAULT_SCOPE,
        frontendFiltering: CONFIG.SEARCH.ENABLE_FRONTEND_FILTERING,
        limit: CONFIG.SEARCH.RESULT_LIMIT,
        returnContent: true,
        contentLength: CONFIG.SEARCH.CONTENT_SNIPPET_LENGTH,
        contentMatchTarget: CONFIG.SEARCH.CONTENT_SNIPPET_MATCH_TARGET,
        contentSnippetsPerResult: CONFIG.SEARCH.CONTENT_SNIPPETS_PER_RESULT,
        matchMarkerStart: CONFIG.SEARCH.MATCH_MARKER_START,
        matchMarkerEnd: CONFIG.SEARCH.MATCH_MARKER_END
      });

      setSearchResults(Array.isArray(payload?.results) ? payload.results : []);
    } catch (err) {
      setSearchError(err?.message || 'Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClose = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    onClose?.();
  };

  const handleSelectResult = (result) => {
    onSelectResult?.(result);
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Search"
      onConfirm={handleRunSearch}
      onCancel={handleClose}
      confirmText={searchLoading ? 'Searching…' : 'Search'}
      cancelText="Close"
      confirmDisabled={searchLoading || !searchQuery.trim()}
    >
      <div className="searchModalInputRow">
        <AppInput
          ref={searchInputRef}
          className="searchInput"
          type="text"
          value={searchQuery}
          onChange={(e) => {
            const nextQuery = e.target.value;
            setSearchQuery(nextQuery);

            if (!CONFIG.SEARCH.RUN_ON_TYPE) return;

            const trimmedQuery = String(nextQuery || '').trim();
            if (trimmedQuery.length < typeMinQueryLength) {
              if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
              }
              setSearchError('');
              setSearchResults([]);
              return;
            }

            if (searchDebounceRef.current) {
              clearTimeout(searchDebounceRef.current);
            }

            searchDebounceRef.current = setTimeout(() => {
              handleRunSearch(nextQuery);
            }, Number(CONFIG.SEARCH.TYPE_DEBOUNCE_MS || 250));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleRunSearch();
            }
          }}
          placeholder="Search docs and chats"
          autoFocus
        />
      </div>

      {searchError && <p className="searchModalError">{searchError}</p>}

      <div className="searchModalMembers searchModalResults">
        {!searchLoading && (!Array.isArray(searchResults) || searchResults.length === 0) && searchQuery.trim().length >= typeMinQueryLength && (
          <p className="placeholder">No results.</p>
        )}

        {(Array.isArray(searchResults) ? searchResults : []).map((result, index) => (
          <div
            key={`${result.doc_id || 'doc'}-${result.chunk_hash || 'chunk'}-${index}`}
            className="searchResultRow"
            role="button"
            tabIndex={0}
            onClick={() => handleSelectResult(result)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelectResult(result);
              }
            }}
          >
            <span className="searchResultInfo">
              <span className="searchResultTop">
                <span className="searchResultName">{result.doc_name || 'Untitled'}</span>
                <span className="searchResultMeta">Score: {Number(result.total_score || 0).toFixed(2)}</span>
              </span>
              {(() => {
                const snippets = Array.isArray(result?.content_snippets) && result.content_snippets.length > 0
                  ? result.content_snippets
                  : [{
                    content_marked: result?.content_marked,
                    content_snippet: result?.content_snippet,
                    chunk_hash: result?.content_source_chunk_hash
                  }];

                const visibleSnippets = snippets
                  .filter(item => String(item?.content_marked || item?.content_snippet || '').trim())
                  .slice(0, Number(CONFIG.SEARCH.CONTENT_SNIPPETS_PER_RESULT || 3));

                if (visibleSnippets.length === 0) return null;

                return (
                  <span className="searchResultSnippets">
                    {visibleSnippets.map((snippetItem, snippetIndex) => (
                      <span
                        key={`${result.doc_id || 'doc'}-${snippetItem?.chunk_hash || 'chunk'}-${snippetIndex}`}
                        className="searchResultSnippet"
                      >
                        {formatSimilarityBadgeText(snippetItem?.content_match) && (
                          <span className="searchResultSimilarityBadge">
                            {formatSimilarityBadgeText(snippetItem?.content_match)}
                          </span>
                        )}
                        {renderMarkedSnippet(
                          snippetItem.content_marked || snippetItem.content_snippet,
                          CONFIG.SEARCH.MATCH_MARKER_START,
                          CONFIG.SEARCH.MATCH_MARKER_END,
                          snippetItem?.content_match?.kind
                        )}
                      </span>
                    ))}
                  </span>
                );
              })()}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
